import { eventInNode } from "./util";
import Notification from "./notification";
import ONBOARDING from "./onboarding";
import { icon, node } from "~/util/dom";
import Connection from "./connection";
import { CONFIG } from "~/config";

const PROFILE_URL = `https://profil.seznam.${CONFIG.TLD}/notifications`;
const OPTIONS = {
	filter: "all",
	icon: "bell",
	hideUntilUsed: false,
};
const eventName = "ontouchstart" in document ? "touchstart" : "mousedown";

export default class Stack {
	button: HTMLButtonElement;

	node: HTMLDivElement;

	interactiveButton: boolean;

	unread: number;

	_visible: boolean;

	_options: any;

	_connection: Connection;

	_listRequestId: string;

	_stateTimeout: number;

	_session: string;

	constructor(connection: Connection, opts: any = {}) {
		const options = {
			...OPTIONS,
			...opts,
		};

		this.interactiveButton = true;
		this._connection = connection;
		this._options = {
			...options,
			icon: options.icon === "email" ? "email2" : options.icon,
		};

		this._listRequestId = ""; // ID nami vyvolane zmeny pri listovani
		this._stateTimeout = 0; // timeout pro dalsi dotaz na user.state

		this._session = "";
		this.unread = 0;

		const id = Math.random();

		this.button = node("button", { type: "button", className: "sn-button", id: `b${id}` });
		this.button.setAttribute("aria-haspopup", "true");
		this.button.setAttribute("aria-controls", `m${id}`);
		this.button.setAttribute("aria-label", options.label);
		this.button.addEventListener("click", this);

		this.node = node("div", { className: "sn-stack", id: `m${id}` });
		this.node.setAttribute("aria-labelledby", `b${id}`);

		document.addEventListener(eventName, this);

		this.visible = false; // na zacatku schovat, nastavi ._visible

		this._updateButton();
		this._visibleButton = false;
	}

	// volano z rodice kdyz dojde ke zmene barvy aktualni session
	onSession(color) {
		this._session = color;

		switch (color) {
			case "green":
				this.node.innerHTML = ""; // at nevidi promo
				break;

			case "orange":
				this._buildOnboarding("orange");
				break;

			case "red":
				this.unread = 0;
				this._buildOnboarding("red");
				if (this._options.hideUntilUsed) { // neprihlaseny -> nebylo pouzito, skryt
					this._visibleButton = false;
				} else { // tlacitko ukaze promo
					this._visibleButton = true;
				}
				break;

			default:
				//
		}

		this._updateButton();
	}

	// volano z rodice pri odpojeni socketu
	onDisconnect() { this._visibleButton = false; }

	// volano z rodice pri prichozich datech
	onMessage(data) {
		const filter = this._options.filter;

		switch (data.type) {
			case "user.channel.list": // inicializace po navazani spojeni: vycet kanalu, stav prihlaseni
				if (this._options.hideUntilUsed) { // vidi ikonku jen pokud existuje pouzity kanal
					const ourChannels = data.channels.filter(ch => this._channelMatches(ch.id));
					const usedChannels = ourChannels.filter(ch => ch.received_messages > 0);

					this._visibleButton = usedChannels.length > 0;
				} else { // vidi ikonku vzdy
					this._visibleButton = true;
				}

				this._getState();
				break;

			case "user.message":
				if (!this._channelMatches(data.message.channel)) {
					return;
				}
				if (this._visible) {
					return;
				} // uz jsme otevreni

				this._visibleButton = true; // mohli jsme byt skryti, nebot z naseho kanalu nikdy nic neprislo
				if (!this.interactiveButton) {
					return;
				} // ukazovani resi nekdo jiny

				this._buildItem(data.message);
				this._openFly();
				break;

			case "user.state":
				if (data.filter !== filter) {
					return;
				}
				this.unread = data.value;
				this._updateButton();

				window.clearTimeout(this._stateTimeout);
				if (data.expires) {
					data.expires = Math.min(data.expires, 86400);
					this._stateTimeout = window.setTimeout(() => this._getState(), 1000*(data.expires + 3));
				}

				this.node.dispatchEvent(new CustomEvent("unread-change", { bubbles: true }));
				break;

			case "user.state.changed":
				this._connection.send("user.state", { filter });

				// jsme otevreni a tuto zmenu nezpusobilo nase otevreni => rekneme si o novy obsah
				if (this._visible && data.rid !== this._listRequestId) {
					this._list();
				}
				break;

			case "user.list":
				if (data.filter !== filter) {
					return;
				}
				this.node.classList.remove("loading");
				this._buildItems(data.value);
				break;

			case "user.insufficient_auth_level":
				this._buildOnboarding("orange"); // pro jistotu, ale uz by melo byt
				break;

			default:
				//
		}
	}

	// volani z notifikace, ktera chce smazat
	onRemove(data) {
		this._connection.send("user.message.remove", { id: data.id });
	}

	// volani z notifikace, ktera chce nahlasit
	onClick(data) {
		this._connection.send("user.message.clicked", { id: data.id });
		this.node.dispatchEvent(new CustomEvent("notification-click", { bubbles: true }));

		if (this.interactiveButton) {
			this.visible = false;
		}
	}

	get visible() {
		return this._visible;
	}

	set visible(visible) {
		this.node.hidden = !visible;

		if (visible) {
			this.button.setAttribute("aria-expanded", "true");
			this.button.classList.add("active");
			this._list();
		} else {
			this.button.classList.remove("active");
			this.button.setAttribute("aria-expanded", "false");
		}

		this._visible = visible;
	}

	handleEvent(event) {
		if (!this.interactiveButton) {
			return;
		}

		switch (event.type) {
			case eventName:
				if (eventInNode(event, this.button)) {
					return;
				}
				if (eventInNode(event, this.node)) {
					return;
				}
				this.visible = false;
				break;

			case "click":
				if (this._visible) {
					this.visible = false;
				} else {
					this.visible = true;
					this._position();
				}
				break;

			default:
				//
		}
	}

	set _visibleButton(visible) {
		this.button.hidden = !visible;
	}

	_getState() {
		this._connection.send("user.state", { filter: this._options.filter });
	}

	_channelMatches(channel) {
		const filter = this._options.filter;

		if (filter === "all") {
			return true;
		}

		const invert = filter.charAt(0) === "-";
		const prefix = invert ? filter.substring(1) : filter;
		let match = channel.indexOf(prefix) === 0;

		if (invert) {
			match = !match;
		}

		return match;
	}

	// eslint-disable-next-line class-methods-use-this
	_buildHeader() {
		const header = node("header");

		header.appendChild(node("h2", {}, "Oznámení"));
		header.appendChild(node("a", { href: PROFILE_URL, target: "_blank" }, "Spravovat oznámení"));

		return header;
	}

	_list() {
		const filter = this._options.filter;

		this.node.classList.add("loading");
		this._listRequestId = this._connection.send("user.list", { filter });
	}

	// otevreni fly notifikace; nevyuziva .visible
	_openFly() {
		this.node.hidden = false;
		this._position();

		setTimeout(() => {
			if (this.visible) {
				return;
			}
			this.node.hidden = true;
		}, 5000);
	}

	_position() {
		if (!this.node.offsetParent) {
			return;
		} // nejsme pripnuti...

		this.node.style.top = `${this.button.offsetHeight + 12}px`;

		// prava hrana licuje s pravou hranou tlacitka + 16px
		let left = this.button.offsetLeft + this.button.offsetWidth - this.node.offsetWidth + 16;

		// leva hrana vuci pruhledu
		const absLeft = this.node.offsetParent.getBoundingClientRect().left + left;

		// vlevo nechceme vyjet z pruhledu
		if (absLeft < 0) {
			left -= absLeft;
		}

		this.node.style.left = `${left}px`;
	}

	_updateButton() {
		const icn = this._options.icon;

		this.button.innerHTML = "";
		this.button.setAttribute("data-dot", `button-${icn}`);

		if (this.unread) {
			const type = this._session === "orange" ? "cut-dot" : "cut-number";
			const svg = icon(`${icn}-${type}`);
			const cn = svg.getAttribute("class");
			const value = this.unread > 9 ? "9+" : this.unread.toString();

			this.button.appendChild(svg);
			svg.setAttribute("class", `${cn} ${type}`);
			this.button.appendChild(node("div", { className: "unread-count" }, value));
		} else {
			this.button.appendChild(icon(icn));
		}
	}

	_buildItems(items) {
		this.node.innerHTML = "";
		this.node.classList.remove("single");

		this.node.appendChild(this._buildHeader());

		const list = node("ul");
		const create = data => new Notification(data, this);
		const append = notification => list.appendChild(notification.node);

		items.map(create).forEach(append);
		this.node.appendChild(list);
	}

	_buildItem(data) {
		this.node.innerHTML = "";
		this.node.classList.add("single");

		const list = node("ul");
		const notification = new Notification(data, this);

		list.appendChild(notification.node);
		this.node.appendChild(list);
	}

	_buildOnboarding(color) {
		this.node.innerHTML = "";

		const div = node("div", { className: `login-${color}` });

		this.node.appendChild(div);
		div.appendChild(node("img", { src: `data:image/svg+xml,${encodeURIComponent(ONBOARDING)}` }));

		switch (color) {
			case "orange":
				div.appendChild(node("h2", {}, "Čekají na vás nová upozornění od Seznamu"));
				div.appendChild(node("p", { innerHTML: "Stačí se přihlásit a uvidíte nové <strong>e-maily</strong> a <strong>důležité zprávy</strong> z našich služeb. Pokud jste <strong>aktivní diskutér,</strong> najdete zde reakce na své příspěvky." }));
				break;

			case "red":
				div.appendChild(node("h2", {}, "Buďte v obraze díky oznámením od Seznamu"));
				div.appendChild(node("p", { innerHTML: "Když se přihlásíte, upozorníme vás na nové <strong>e-maily</strong> i <strong>důležité zprávy</strong> z našich služeb. Pokud jste <strong>aktivní diskutér,</strong> najdete zde reakce na své příspěvky." }));
				break;

			default:
				//
		}

		if (window.login) {
			const link = window.login.link();

			link.textContent = "Přihlásit se";
			div.appendChild(link);
			link.addEventListener("click", () => {
				if (!this.interactiveButton) {
					return;
				}
				this.visible = false;
			});
		}
	}
}
